import type { CreateGameInput, UpdateGameInput } from '@gc/shared';

/**
 * The Games drawer's wire payload, extracted so it is testable in plain node
 * (this workspace ships no DOM emulation, so a submit can never be clicked in a
 * test — the builder IS the seam, exactly as `sessionLogic.ts` is for the
 * reservation drawer).
 */

/**
 * A media id as the drawer holds it. THREE states, not two:
 *
 * - `undefined` — untouched. The key is omitted and the stored asset stands.
 * - `null` — the trash icon was pressed. `null` is POSTED, which is what
 *   actually clears the column.
 * - a string — freshly uploaded.
 *
 * The two-state version shipped a bug: `...(coverMediaAssetId ? { coverMediaAssetId } : {})`
 * cannot tell "not touched" from "cleared", so both sent nothing. Staff pressed
 * the trash icon, watched the preview disappear, saved — and the old cover was
 * still there on reload, because the request never mentioned it. The API
 * schemas are `.optional().nullable()` precisely so this distinction survives
 * the wire.
 */
export type MediaIdEdit = string | null | undefined;

export interface GameDraft {
  title: string;
  company: string;
  executablePath: string;
  /** Already reduced from the override map by `toDevicePathPayload`. */
  devicePaths: NonNullable<UpdateGameInput['devicePaths']>;
  coverMediaAssetId: MediaIdEdit;
  trailerMediaAssetId: MediaIdEdit;
}

/**
 * Build the `POST /settings/games` / `PATCH /settings/games/:id` body.
 *
 * One builder for both verbs: create and update take the same field set, and
 * the only real difference — an omitted key means "leave it" on PATCH and
 * "there is none" on POST — is the same behaviour either way for a drawer that
 * starts empty when creating.
 */
export function buildGamePayload(draft: GameDraft): CreateGameInput & UpdateGameInput {
  const payload: CreateGameInput & UpdateGameInput = {
    title: draft.title,
    company: draft.company.trim() || null,
    executablePath: draft.executablePath.trim() || null,
    devicePaths: draft.devicePaths,
  };

  // `!== undefined`, never truthiness: `null` is a real instruction here.
  if (draft.coverMediaAssetId !== undefined) payload.coverMediaAssetId = draft.coverMediaAssetId;
  if (draft.trailerMediaAssetId !== undefined) {
    payload.trailerMediaAssetId = draft.trailerMediaAssetId;
  }

  return payload;
}
